import { IntlShape } from 'react-intl'
import { formatCount, NO_VALUE } from '../UrgencySummary/urgencySummaryFormat'

/**
 * Formato de los números de la pantalla, en un solo lugar. La cantidad va siempre con separador de miles (`formatCount`, el
 * mismo de las otras pantallas de reportes) y los porcentajes, las horas y los minutos, siempre con un decimal ("72,0 %" y no
 * "72 %"): así dos filas seguidas no se ven con decimales desparejos.
 */

export { formatCount, NO_VALUE }

const ONE_DECIMAL = { useGrouping: 'always', minimumFractionDigits: 1, maximumFractionDigits: 1 } as const

/** `percent` ya está en % (72 = 72 %): "72,0 %", o "—" si no hay dato. */
export function formatPercent(intl: IntlShape, percent: number | null | undefined): string {
  if (percent === null || percent === undefined) return NO_VALUE
  return `${intl.formatNumber(percent, ONE_DECIMAL)} %`
}

/** Qué parte de `total` es `part`, en % (sin redondear); null si no hay total (0 o sin dato). */
export function shareOf(part: number | null | undefined, total: number | null | undefined): number | null {
  if (part === null || part === undefined || total === null || total === undefined || total <= 0) return null
  return (part / total) * 100
}

/** Horas con un decimal y la unidad: "412,5 h", o "—" si no hay dato. */
export function formatHours(intl: IntlShape, hours: number | null | undefined): string {
  if (hours === null || hours === undefined) return NO_VALUE
  return intl.formatMessage({ id: 'OperatingRoomIndicatorsHoursValue' }, { value: intl.formatNumber(hours, ONE_DECIMAL) })
}

/** Número de horas sin unidad (para columnas y ejes que ya la dicen): "412,5". */
export function formatHoursPlain(intl: IntlShape, hours: number | null | undefined): string {
  return hours === null || hours === undefined ? NO_VALUE : intl.formatNumber(hours, ONE_DECIMAL)
}

/** Minutos como duración legible: "45 min" o "1 h 05 min"; "—" si no hay dato. */
export function formatDuration(intl: IntlShape, minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return NO_VALUE
  const total = Math.round(minutes)
  if (total < 60) return intl.formatMessage({ id: 'OperatingRoomIndicatorsMinutesValue' }, { value: formatCount(intl, total) })
  return intl.formatMessage({ id: 'OperatingRoomIndicatorsHoursMinutesValue' }, {
    hours: Math.floor(total / 60), minutes: String(total % 60).padStart(2, '0'),
  })
}

/** Cantidad, o "—" si no hay dato (celda de tabla). */
export function formatCountOrDash(intl: IntlShape, value: number | null | undefined): string {
  return value === null || value === undefined ? NO_VALUE : formatCount(intl, value)
}

/** Minutos con su signo: "+8 min", "−5 min", "0 min" (la diferencia entre lo real y lo programado); "—" si no hay dato. */
export function formatSignedDuration(intl: IntlShape, minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return NO_VALUE
  const total = Math.round(minutes)
  if (total === 0) return formatDuration(intl, 0)
  return `${total > 0 ? '+' : '−'}${formatDuration(intl, Math.abs(total))}`
}
